import { useCallback, useEffect, useRef, useState } from 'react';
import { TopBar } from './TopBar';
import { AccordionMenu } from './AccordionMenu';
import { TabPanel } from './TabPanel';
import { SidebarFooter } from './SidebarFooter';
import { useSidebar } from '@/hooks/useSidebar';
import { SIDEBAR_MAX_WIDTH, SIDEBAR_MIN_WIDTH } from '@/app/providers/SidebarProvider';
import { ClientOptionsHost } from '@/components/client-options';
import { FeedbackHost } from '@/components/feedback';

const COLLAPSED_WIDTH = 48;

export function AppShell() {
  const { isExpanded, width, setWidth } = useSidebar();
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; startWidth: number } | null>(null);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      const state = dragStateRef.current;
      if (!state) return;
      setWidth(state.startWidth + (e.clientX - state.startX));
    },
    [setWidth],
  );

  const handleMouseUp = useCallback(() => {
    dragStateRef.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!isDragging) return;
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const startDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    dragStateRef.current = { startX: e.clientX, startWidth: width };
    setIsDragging(true);
  };

  const handleSeparatorKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      setWidth(width - 16);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      setWidth(width + 16);
    }
  };

  const sidebarWidth = isExpanded ? width : COLLAPSED_WIDTH;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Zone 1: Top Bar */}
      <TopBar />

      {/* Zone 2 + 3: Sidebar + Tab Panel */}
      <div className={isDragging ? 'flex flex-1 select-none overflow-hidden' : 'flex flex-1 overflow-hidden'}>
        <nav
          aria-label="Main"
          data-sidebar
          style={{ width: sidebarWidth }}
          className="relative flex flex-shrink-0 flex-col overflow-hidden h-full bg-sidebar text-sidebar-fg"
        >
          <div className="flex-1 overflow-hidden">
            <AccordionMenu />
          </div>
          <SidebarFooter collapsed={!isExpanded} />
          {isExpanded && (
            <div
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize sidebar"
              aria-valuemin={SIDEBAR_MIN_WIDTH}
              aria-valuemax={SIDEBAR_MAX_WIDTH}
              aria-valuenow={width}
              tabIndex={0}
              onMouseDown={startDrag}
              onKeyDown={handleSeparatorKeyDown}
              className="absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize hover:bg-primary/40 focus:bg-primary/60 focus:outline-none"
            />
          )}
        </nav>

        <main className="flex flex-1 flex-col overflow-hidden">
          <TabPanel />
        </main>
      </div>

      {/* SP13.0 — Global launchable dialog. Pattern A — mounts only when
          useClientOptions().launch() is active; null otherwise. Sentinel
          lives at shell scope (not inside any tool) so it survives tool
          tab closes. */}
      <ClientOptionsHost />

      {/* Report feedback: shell-scope host for the floating panel; renders nothing while closed. */}
      <FeedbackHost />
    </div>
  );
}
